// src/ProtectedRoute.tsx
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Preloader from '@/components/Preloader';

interface ProtectedRouteProps {
    children: React.ReactNode;
    allowedGroups?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    allowedGroups = []
}) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const [isChecking, setIsChecking] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);

    useEffect(() => {
        const checkAccess = async () => {
            // Nếu đang loading thì đợi, không làm gì cả
            if (isLoading) {
                return;
            }

            // Nếu không đang loading và không đăng nhập, không có quyền truy cập
            if (!isAuthenticated || !user) {
                setHasAccess(false);
                setIsChecking(false);
                return;
            }

            // Nếu không cần kiểm tra nhóm, cho phép truy cập
            if (!allowedGroups || allowedGroups.length === 0) {
                setHasAccess(true);
                setIsChecking(false);
                return;
            }

            // Lấy danh sách nhóm của người dùng
            const userGroups = user.groups?.map(group => group.code) || [];

            // Kiểm tra nếu người dùng thuộc nhóm ADMIN_USERS
            if (userGroups.includes('ADMIN_USERS')) {
                setHasAccess(true);
                setIsChecking(false);
                return;
            }

            // Kiểm tra xem người dùng có thuộc ít nhất một nhóm được phép
            const hasRequiredGroup = allowedGroups.some(group => userGroups.includes(group));

            setHasAccess(hasRequiredGroup);
            setIsChecking(false);
        };

        checkAccess();
    }, [isAuthenticated, isLoading, user, allowedGroups]);

    // Chỉ hiển thị preloader khi đang kiểm tra hoặc đang loading
    if (isLoading || isChecking) {
        return <Preloader />;
    }

    // Nếu không đăng nhập, chuyển đến trang login
    if (!isAuthenticated) {
        const currentPath = window.location.pathname;

        // Chỉ thêm redirectTo nếu không phải trang login hoặc register
        if (currentPath !== '/login' && currentPath !== '/register') {
            return <Navigate to={`/login`} replace />;
        } else {
            return <Navigate to="/login" replace />;
        }
    }

    // Nếu không có quyền truy cập
    if (!hasAccess) {
        return <Navigate to="/unauthorized" replace />;
    }

    // Có quyền truy cập
    return <>{children}</>;
};

export default ProtectedRoute;